
import { Component, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { SystemModule } from "@/store/modules";
import notifications from "../lib/notifications";
import permissions from "@/security/permissions";
import {
  CustomerOnHoldResource,
  GetCustomerOnHoldRequest
} from "@/models/api/system/customerOnHold";
import { formatDate } from "@/utils";

const systemModule = getModule(SystemModule);

@Component
export default class CustomerOnHold extends Vue {
  permissions = permissions;
  selectedBillTo: string = null;
  loading: boolean = false;
  records: CustomerOnHoldResource[] = [];
  hasLoadedRecords: boolean = false;
  totalItemCount: number = 0;
  pageNumber: number = 1;
  pageSize: number = 15;
  isOnHold: boolean = false;
  showConfirmModal: boolean = false;
  pendingAction: "place" | "remove" | null = null;

  @Watch("selectedBillTo")
  async selectedBillToChanged(newBillTo: string) {
    const previousPage = this.pageNumber;
    this.pageNumber = 1;

    if (!newBillTo) {
      this.records = [];
      this.hasLoadedRecords = false;
      this.totalItemCount = 0;
      return;
    }

    if (previousPage === 1) {
      await this.loadCustomersOnHold();
    }
  }

  @Watch("pageNumber")
  async pageNumberChanged() {
    if (this.selectedBillTo) {
      await this.loadCustomersOnHold();
    }
  }

  async loadCustomersOnHold() {
    this.loading = true;
    try {
      const request: GetCustomerOnHoldRequest = {
        billToCompanyId: this.selectedBillTo,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize
      };
      const response = await systemModule.getCustomerOnHold(request);
      this.records = response.orders || [];
      this.isOnHold = response.isOnHold || false;
      this.totalItemCount = response.totalItemCount || 0;
      this.hasLoadedRecords = true;
    } catch (ex) {
      notifications.error("Customers On Hold", "Failed to load customers on hold.", ex);
      this.records = [];
      this.isOnHold = false;
      this.hasLoadedRecords = false;
      this.totalItemCount = 0;
    } finally {
      this.loading = false;
    }
  }

  formatScheduledDeliveryDate(value: Date | string | null): string {
    return formatDate(value);
  }

  isNotScheduled(value: Date | string | null): boolean {
    if (!value) return true;
    const dateStr = typeof value === "string" ? value : new Date(value).toISOString();
    return dateStr.startsWith("2049-12-31");
  }

  async placeOnHold() {
    try {
      await systemModule.placeCustomerOnHold(this.selectedBillTo);
      this.isOnHold = true;
      notifications.success("Customers On Hold", "Customer placed on hold successfully.");
    } catch (ex) {
      notifications.error("Customers On Hold", "Failed to place customer on hold.", ex);
    }
  }

  async removeFromHold() {
    try {
      await systemModule.removeCustomerFromHold(this.selectedBillTo);
      this.isOnHold = false;
      notifications.success("Customers On Hold", "Customer removed from hold successfully.");
    } catch (ex) {
      notifications.error("Customers On Hold", "Failed to remove customer from hold.", ex);
    }
  }

  showPlaceConfirmation() {
    this.pendingAction = "place";
    this.showConfirmModal = true;
  }

  showRemoveConfirmation() {
    this.pendingAction = "remove";
    this.showConfirmModal = true;
  }

  async confirmAction() {
    if (this.pendingAction === "place") {
      await this.placeOnHold();
    } else if (this.pendingAction === "remove") {
      await this.removeFromHold();
    }
    this.pendingAction = null;
  }

  cancelAction() {
    this.pendingAction = null;
  }

}
